// One image slot for the template editor: the asset field plus the picker it
// opens. A CONTROLLED field — it owns no server state and saves nothing on
// its own; the chosen asset's id goes up through `onChange` into the
// editor's single form, so picking an image changes the draft (and the live
// preview) and nothing else until Save. Uploading still reaches the asset
// service immediately, because that is what creating an asset means; only
// the assignment waits.
//
// No print-resolution rules: unlike vouchers' ticket artwork (which this
// family came from), signage has no print constraint, so neither
// ImageAssetField nor ImageAssetPicker below is passed a `lowRes` prop at all.

import {
    type AssetFolderSummary,
    Dialog,
    ImageAssetField,
    ImageAssetPicker,
    type ImageAssetSummary,
    toast,
} from "@pej/kore/components";
import { useState } from "react";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { toImageAssetSummary } from "@/features/assets/asset-projections";
import { useAssetById, useFolders } from "@/features/assets/asset-queries";
import { useAssetPickerProps } from "@/features/assets/use-asset-picker-props";
import { useAssetUpload } from "@/features/assets/use-asset-upload";

/** The post-upload toast names where the asset landed — "Unsorted" for `null`,
 *  or a folder that's since been deleted between the upload starting and
 *  finishing (a narrow race, but one the lookup has to resolve one way). */
export function uploadDestinationName(folders: AssetFolderSummary[], folderId: string | null): string {
    return folders.find((folder) => folder.id === folderId)?.name ?? "Unsorted";
}

export interface ArtworkSlotProps {
    /** The asset id currently in the draft; "" for none. */
    value: string;
    onChange: (assetId: string) => void;
    /** Omit on a single-field card whose own title already names the field. */
    fieldLabel?: string;
    pickerTitle: string;
    /** Title of the unavailable state; omit when a caption outside already names the slot. */
    unavailableLabel?: string;
}

export function ArtworkSlot({ value, onChange, fieldLabel, pickerTitle, unavailableLabel }: ArtworkSlotProps) {
    const ctx = useAssetClientContext();
    const [open, setOpen] = useState(false);

    // A just-uploaded asset is displayable before the library query can find
    // it, so its summary is held here and used whenever it is what the draft
    // points at. Any other id falls through to the lookup.
    const [pendingAsset, setPendingAsset] = useState<ImageAssetSummary | null>(null);

    const folders = useFolders(ctx);
    const pendingMatches = pendingAsset !== null && pendingAsset.id === value;
    const { asset: fetchedAsset, notFound } = useAssetById(ctx, pendingMatches || !value ? null : value);
    const asset = pendingMatches ? pendingAsset : fetchedAsset;

    function pick(picked: ImageAssetSummary) {
        setPendingAsset(picked);
        onChange(picked.id);
    }

    const { phase, start, cancel } = useAssetUpload(ctx, (uploaded) => {
        // Always an image: the field refuses a non-image before it uploads.
        const summary = toImageAssetSummary(uploaded, null);
        if (summary) pick(summary);
        toast.success(`Added to ${uploadDestinationName(folders, uploaded.folderId)}`);
    });
    const pickerProps = useAssetPickerProps(ctx, folders.length);

    if (!ctx) {
        // Supported state, not a failure: a deployment with no asset API
        // configured still edits every other part of the template.
        return (
            <div className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                {unavailableLabel && <div className="mb-1 font-medium text-foreground">{unavailableLabel}</div>}
                <p>Image uploads aren't available here yet.</p>
            </div>
        );
    }

    return (
        <div className="min-w-0 flex-1">
            <ImageAssetField
                label={fieldLabel}
                value={asset}
                phase={phase}
                canManage
                onFiles={(files) => start(files, null)}
                onOpenPicker={() => setOpen(true)}
                onClear={() => {
                    setPendingAsset(null);
                    onChange("");
                }}
                onCancelUpload={cancel}
                folders={folders}
            />
            {notFound && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                    This image is no longer in the library. Choose another, or clear it.
                </p>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <Dialog.Content
                    showCloseButton={false}
                    className="w-fit max-w-[calc(100vw-2rem)] gap-0 p-0"
                    aria-label={pickerTitle}
                >
                    {pickerProps && (
                        <ImageAssetPicker
                            {...pickerProps}
                            title={pickerTitle}
                            inUseAssetId={value || null}
                            onUpload={(files, folderId) => start(files, folderId)}
                            onSelect={(picked) => {
                                pick(picked);
                                setOpen(false);
                            }}
                            onPreview={() => undefined}
                            onClose={() => setOpen(false)}
                        />
                    )}
                </Dialog.Content>
            </Dialog>
        </div>
    );
}
